import * as React from 'react';

import { useInView } from '@cian/valuation-utils-component';

import { EPaginationStatus } from '../../slices/recommendationsSlice';
import type { useRecommendations } from './useRecommendations';

type TInfiniteRecommendationsControls = Pick<
  ReturnType<typeof useRecommendations>,
  'onShow' | 'recommendations' | 'hasMore' | 'paginationStatus' | 'onLoadMore'
>;

/**
 * Бизнес-логика бесконечной подгрузки рекомендаций.
 * - Триггерит догрузку, когда sentinel появляется во вьюпорте и пагинация в Idle.
 * - Дёргает аналитику показа `onShow` при росте списка (после успешной догрузки).
 *
 * Возвращает `sentinelRef` — его нужно повесить на DOM-элемент, замыкающий список.
 */
export const useInfiniteRecommendations = ({
  onShow,
  recommendations,
  hasMore,
  paginationStatus,
  onLoadMore,
}: TInfiniteRecommendationsControls) => {
  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const recommendationsCountRef = React.useRef(recommendations.length);

  React.useEffect(() => {
    if (inView && hasMore && paginationStatus === EPaginationStatus.Idle) {
      onLoadMore();
    }
  }, [hasMore, inView, onLoadMore, paginationStatus]);

  React.useEffect(() => {
    const previousRecommendationsCount = recommendationsCountRef.current;

    if (recommendations.length > previousRecommendationsCount && previousRecommendationsCount > 0) {
      onShow();
    }

    recommendationsCountRef.current = recommendations.length;
  }, [recommendations.length, onShow]);

  return { sentinelRef };
};
